import { Mechanics } from "game-common/mechanics/mechanics"
import { Coordinate, Entity, EntityId, TileSize } from "game-common/models"
import { Container, Graphics } from "pixi.js"
import { ClientGameLogic } from "../../../client_game_logic"
import { IWorldOverlaySurface } from "../../../client_models"
import { ClientRenderable } from "./entity_rendering_manager"
import { HoldTimer } from "./gui/hold_timer"
import { RescueHint } from "./gui/rescue_hint"
import { BaseRenderable } from "./sprites/base_renderable"
import { ScalingParams } from "../../../client_util"
import { BonecallerWarning } from "./effects/bonecaller_warning"
import { BonecallerShrine, BonecallerEmergence } from "./effects/bonecaller_shrine"

export class WorldOverlayItem extends BaseRenderable {}

const SCALING_CONSTANT = ScalingParams.surfaceZoom
export class WorldOverlaySurface extends Graphics implements IWorldOverlaySurface {
    private entityIdToOverlayItem: Map<EntityId, ClientRenderable> = new Map()
    // Countdown dials for held interactions, one per entity somebody is currently holding (see
    // HoldTimer). Kept here rather than inside AvatarHud because a hold target need not be a
    // character: the hud belongs to the LPC sprites, while EVERY entity has an overlay item, which
    // is already parked above its head and already follows it.
    private holdTimers: Map<EntityId, HoldTimer> = new Map()
    // The "hold on her to help her up" prompt over an injured rescue survivor, kept here for the
    // same reason as the dial above: it hangs off the entity's overlay item, so it follows her
    // without any per-frame arithmetic. At most one of these exists in a room (there is one
    // survivor per camp), and only while the server is asking for it.
    private rescueHints: Map<EntityId, RescueHint> = new Map()
    private bonecallerWarnings: Map<EntityId, BonecallerWarning> = new Map()
    private bonecallerShrines: Map<EntityId, BonecallerShrine> = new Map()
    private bonecallerEmergences: Map<EntityId, BonecallerEmergence> = new Map()

    logic: ClientGameLogic

    constructor(logic: ClientGameLogic, private groundSurface?: Container) {
        super()
        this.logic = logic
        this.exposeHoldTimerTrigger()
        // Overlay items paint by zIndex so a speaking entity can lift its HUD (chat bubble) above the
        // nameplates/role glyphs of the entities around it -- see AvatarHud.liftOverlay. Items all rest
        // at zIndex 0, where pixi's stable sort keeps the old insertion order.
        this.sortableChildren = true
    }

    offer = (entity: Entity): WorldOverlayItem => {
        const renderable = new WorldOverlayItem()
        renderable.zIndex = 0
        // A renderable can be rebuilt under a live entity (an appearance change), which lands here
        // again with a fresh item. Any dial is still inside the OLD one, where nothing will ever
        // move it again; drop it and the next message raises it on the new item.
        this.dropHoldTimer(entity.entityId)
        this.dropRescueHint(entity.entityId)
        this.entityIdToOverlayItem.set(entity.entityId, renderable)

        const f = 32 * SCALING_CONSTANT
        renderable.beginFill(0xff0000, 0.01)
        renderable.drawRect(0, 0, f, f)
        renderable.endFill()

        renderable.width = f
        renderable.height = f
        this.addChild(renderable)

        return renderable
    }

    remove = (entity: Entity) => {
        if (!entity) {
            return
        }
        this.dropBonecallerWarning(entity.entityId)
        this.dropRitualArt(entity.entityId)
        const renderable = this.entityIdToOverlayItem.get(entity.entityId)
        if (!renderable) {
            return
        }

        renderable.remove()
        this.entityIdToOverlayItem.delete(entity.entityId)
        this.dropHoldTimer(entity.entityId)
        this.dropRescueHint(entity.entityId)
    }

    // An entity leaving takes its overlay item with it, but only DETACHES it (BaseRenderable.remove),
    // so a dial left inside would keep its Graphics and its Text for the rest of the session.
    /**
     * `holdTimer("lord")` in the console raises a countdown dial over the first entity whose name
     * matches, for as long as given, so the thing can be looked at and tuned.
     *
     * The real trigger is a held interaction on a rescue survivor, which takes a wave-3 clearance and
     * a fight at the wreck to reach -- there would otherwise be no way to check the dial's placement,
     * its colours or its endings against real art. It draws only: the server knows nothing about it,
     * and the next message from that entity takes it straight back down.
     */
    private exposeHoldTimerTrigger = () => {
        if (typeof window === "undefined") {
            return
        }
        ;(window as any).holdTimer = (name?: string, durationMs: number = 2000): string => {
            const match = this.logic?.entityManager
                ?.collect((e: Entity) => !name || (e.name || "").toLowerCase().includes(name.toLowerCase()))
                .find((e: Entity) => this.entityIdToOverlayItem.has(e.entityId))
            if (!match) {
                return `[holdTimer] no on-screen entity matching ${name ?? "(any)"}`
            }
            this.updateHoldTimer({ ...match, holdRemainingMs: durationMs, holdDurationMs: durationMs } as Entity)
            return `[holdTimer] ${match.name || match.entityId} for ${durationMs}ms`
        }
    }

    private dropHoldTimer = (entityId: EntityId) => {
        const timer = this.holdTimers.get(entityId)
        if (!timer) {
            return
        }
        this.holdTimers.delete(entityId)
        timer.parent?.removeChild(timer)
        if (!timer.destroyed) {
            timer.destroy({ children: true })
        }
    }

    private dropRescueHint = (entityId: EntityId) => {
        const hint = this.rescueHints.get(entityId)
        if (!hint) {
            return
        }
        this.rescueHints.delete(entityId)
        hint.parent?.removeChild(hint)
        if (!hint.destroyed) {
            hint.destroy({ children: true })
        }
    }

    updateEntityId = (originalEntityId: EntityId, updatedEntityId: EntityId) => {
        this.entityIdToOverlayItem.set(updatedEntityId, this.entityIdToOverlayItem.get(originalEntityId))
        this.entityIdToOverlayItem.delete(originalEntityId)
        const warning = this.bonecallerWarnings.get(originalEntityId)
        if (warning) {
            this.bonecallerWarnings.set(updatedEntityId, warning)
            this.bonecallerWarnings.delete(originalEntityId)
        }
        for (const map of [this.bonecallerShrines, this.bonecallerEmergences]) {
            const art = map.get(originalEntityId)
            if (art) { (map as Map<EntityId, Graphics>).set(updatedEntityId, art); map.delete(originalEntityId) }
        }
        // The dial travels with the item it is parented to, so only its key moves.
        const timer = this.holdTimers.get(originalEntityId)
        if (timer) {
            this.holdTimers.set(updatedEntityId, timer)
            this.holdTimers.delete(originalEntityId)
        }
        const hint = this.rescueHints.get(originalEntityId)
        if (hint) {
            this.rescueHints.set(updatedEntityId, hint)
            this.rescueHints.delete(originalEntityId)
        }
    }

    updateEntity = (entity: Entity) => {
        if (!entity) {
            return
        }

        const renderable = this.entityIdToOverlayItem.get(entity.entityId)
        if (!renderable) {
            return
        }

        const yScaler = Mechanics.entity.body.isShort(entity) ? 0.75 : 1.25
        renderable.visible = entity.visible
        renderable.x = entity.location.x * SCALING_CONSTANT - TileSize * 0.25 * SCALING_CONSTANT
        renderable.y = entity.location.y * SCALING_CONSTANT - TileSize * yScaler * SCALING_CONSTANT
        this.updateBonecallerWarning(entity)
        this.updateRitualArt(entity)
    }

    private updateRitualArt = (entity: Entity) => {
        if (entity.bonecallerRitual) {
            let shrine = this.bonecallerShrines.get(entity.entityId)
            if (!shrine) {
                // Ground tiles sort below 50 and characters above 100 + feet-y. The HUD overlay
                // sits above the whole world, so a negative overlay zIndex still covers bodies.
                shrine = new BonecallerShrine(this.groundSurface ? 1 : SCALING_CONSTANT)
                shrine.zIndex = this.groundSurface ? 50 : -2
                ;(this.groundSurface || this).addChild(shrine)
                this.bonecallerShrines.set(entity.entityId, shrine)
            }
            shrine.update(entity)
        }
        if (entity.bonecallerEmergenceRemainingMs) {
            let emergence = this.bonecallerEmergences.get(entity.entityId)
            if (!emergence) {
                emergence = new BonecallerEmergence(); emergence.zIndex = -1
                this.addChild(emergence); this.bonecallerEmergences.set(entity.entityId, emergence)
            }
            emergence.update(entity)
        } else {
            const emergence = this.bonecallerEmergences.get(entity.entityId)
            if (emergence) { this.bonecallerEmergences.delete(entity.entityId); emergence.destroy() }
        }
    }

    private dropRitualArt = (id: EntityId) => {
        for (const map of [this.bonecallerShrines, this.bonecallerEmergences]) {
            const art = map.get(id)
            if (!art) continue
            map.delete(id); art.parent?.removeChild(art); art.destroy()
        }
    }

    updateBonecallerWarning = (entity: Entity) => {
        if (!entity.bonecallerCombatRadius) {
            this.dropBonecallerWarning(entity.entityId)
            return
        }
        let warning = this.bonecallerWarnings.get(entity.entityId)
        if (!warning) {
            warning = new BonecallerWarning()
            warning.zIndex = -1
            this.addChild(warning)
            this.bonecallerWarnings.set(entity.entityId, warning)
        }
        warning.update(entity)
    }

    private dropBonecallerWarning = (id: EntityId) => {
        const warning = this.bonecallerWarnings.get(id)
        if (!warning) return
        this.bonecallerWarnings.delete(id)
        warning.parent?.removeChild(warning)
        warning.destroy({ children: true })
    }

    animateBonecallerWarnings = () => {
        for (const map of [this.bonecallerShrines, this.bonecallerEmergences]) {
            map.forEach((art, id) => {
                const item = this.entityIdToOverlayItem.get(id)
                if (!item || art.destroyed) { this.dropRitualArt(id); return }
                art.visible = item.visible
                if (art.visible) art.animate()
            })
        }
        this.bonecallerWarnings.forEach((warning, id) => {
            const item = this.entityIdToOverlayItem.get(id)
            if (!item || warning.destroyed) { this.dropBonecallerWarning(id); return }
            const player = this.logic?.playerEntity
            warning.setBoundaryVisible(item.visible && (!player || warning.viewerInside(player.location)))
            warning.animate(Date.now(), item.visible)
        })
    }

    getEntityRenderable = (entityId: EntityId): ClientRenderable => {
        return this.entityIdToOverlayItem.get(entityId)
    }

    /**
     * The server's verdict on whether a held interaction is running against this entity, straight
     * off the wire (see Message.holdRemainingMs). A remaining time raises the dial and keeps it
     * anchored; no remaining time ends whichever one is up.
     *
     * Called for every entity message, so it has to be cheap and idempotent in the common case of
     * no hold at all: that is a single failed Map lookup.
     */
    updateHoldTimer = (entity: Entity) => {
        const existing = this.holdTimers.get(entity.entityId)
        if (!(entity.holdRemainingMs > 0)) {
            // The dial is not dropped here -- `stop` starts its ending, and animateHoldTimers takes
            // it down once that has played. Stopping twice is a no-op, which matters because this
            // arrives on every message for as long as the entity is in range.
            existing?.stop()
            return
        }
        const timer = existing || this.attachHoldTimer(entity.entityId)
        timer?.setRemaining(entity.holdRemainingMs, entity.holdDurationMs)
    }

    /**
     * The server's verdict on whether the "hold on her to help her up" prompt belongs over this
     * entity, straight off the wire (see Message.rescueHelpHint). Raising it is the rescue's
     * decision, never this surface's: the hold is refused while a skeleton still stands over the
     * wreck, so the prompt waits for the fight to end.
     *
     * Called for every entity message, so -- like updateHoldTimer above -- it has to be cheap and
     * idempotent in the common case of no prompt at all: one failed Map lookup.
     */
    updateRescueHint = (entity: Entity) => {
        const existing = this.rescueHints.get(entity.entityId)
        if (!entity.rescueHelpHint) {
            // Not dropped here: `stop` starts its ending and animateRescueHints takes it down once
            // that has played. Stopping twice is a no-op, which matters because this arrives on
            // every message for as long as the entity is in range.
            existing?.stop()
            return
        }
        const hint = existing || this.attachRescueHint(entity.entityId)
        hint?.show()
    }

    private attachRescueHint = (entityId: EntityId): RescueHint | undefined => {
        const item = this.entityIdToOverlayItem.get(entityId)
        if (!item) {
            return undefined
        }
        const hint = new RescueHint()
        // Added after the hud, so it draws over the name plate rather than under it.
        item.addChild(hint)
        this.rescueHints.set(entityId, hint)
        return hint
    }

    /**
     * Sweep the live prompt, once a frame. There is at most one in a room and only during the beat
     * between the wreck being cleared and the survivor being helped up, so this is empty almost
     * always -- the early return is what keeps it off the frame budget the rest of the time.
     */
    animateRescueHints = () => {
        if (this.rescueHints.size === 0) {
            return
        }
        const now = Date.now()
        this.rescueHints.forEach((hint, entityId) => {
            // An entity that left took its overlay item -- and this prompt with it -- so there is
            // nothing left to animate and the entry would otherwise be held for the session.
            if (hint.destroyed || !this.entityIdToOverlayItem.has(entityId)) {
                this.dropRescueHint(entityId)
                return
            }
            if (!hint.animate(now)) {
                this.dropRescueHint(entityId)
            }
        })
    }

    private attachHoldTimer = (entityId: EntityId): HoldTimer | undefined => {
        const item = this.entityIdToOverlayItem.get(entityId)
        if (!item) {
            return undefined
        }
        const timer = new HoldTimer()
        // Added after the hud, so it draws over the name plate it sits above.
        item.addChild(timer)
        this.holdTimers.set(entityId, timer)
        return timer
    }

    /**
     * Sweep every live dial, once a frame. A hold lasts a couple of seconds and only one entity at
     * a time is usually being held, so this is empty almost always and tiny when it is not.
     */
    animateHoldTimers = () => {
        if (this.holdTimers.size === 0) {
            return
        }
        const now = Date.now()
        this.holdTimers.forEach((timer, entityId) => {
            // An entity that left the room took its overlay item -- and this dial with it -- down
            // with it, so there is nothing left to animate and the entry would otherwise be held
            // for the session.
            if (timer.destroyed || !this.entityIdToOverlayItem.has(entityId)) {
                this.dropHoldTimer(entityId)
                return
            }
            if (!timer.animate(now)) {
                this.dropHoldTimer(entityId)
            }
        })
    }

    computeLocation = (x: number, y: number): Coordinate => {
        return {
            x: x * SCALING_CONSTANT,
            y: y * SCALING_CONSTANT,
        }
    }

    // The inverse of computeLocation: a point in this surface's local frame back to a fine world
    // coordinate. With `toLocal` in front of it this turns a screen position into the spot on the
    // ground under it -- which is how the location picker resolves where the player clicked,
    // straight off pixi's own transforms rather than off the camera arithmetic.
    worldLocation = (x: number, y: number): Coordinate => {
        return {
            x: x / SCALING_CONSTANT,
            y: y / SCALING_CONSTANT,
        }
    }
}
